import {
  MAX_VERSIONS,
  describeVersions,
  versionCountOf,
  versionLabel,
  versionsProblem,
  versionQuantities,
  withVersionCount,
  withVersionSplit,
  type VersionSplitMode,
  type VersionsValue,
} from "@shared-core/domain/line-versions";
import { RadioGroup, RadioGroupItem } from "../../primitives/radio-group";

/**
 * The version control (order to invoice doc 108 §3.1; doc 44 §3.1): how many versions the line has, 1 to 26, and how
 * its quantity is shared between them. One version is the default and shows nothing more. From two it offers Split
 * evenly (the default; the share is shown) or Enter quantities (one box per version, which must add up to the line).
 * The versions are labelled A, B, C… here; they are renamed later on the quote or the order, never here.
 *
 * Controlled: the page or widget holds `countRaw` as typed (the item's `Versions` field) and the rest of the value. The
 * even share and the sum are previews of the backend's rule (`@shared-core/domain/line-versions`); `errors` are the
 * backend's own field errors for these inputs, bound beside them, and win over a preview when both speak.
 */
export interface VersionsControlErrors {
  /** On the count: the item field's own constraint message, or the backend's. */
  count?: string;
  /** On the split choice (`version_split`). */
  split?: string;
  /** On the quantities (`version_quantities`), or on the line quantity when it is too few for the versions. */
  quantities?: string;
}

export function VersionsControl({
  label,
  countRaw,
  value,
  quantity,
  quantityStep,
  errors = {},
  disabled = false,
  idPrefix = "versions",
  onCountChange,
  onValueChange,
}: {
  label: string;
  /** The count as typed: kept as text so a half-typed number is not rewritten under the cursor. */
  countRaw: string;
  value: VersionsValue;
  /** The line's quantity in all: the versions share it. */
  quantity: number;
  /** The step the item's quantities move in; the even split rounds to it. */
  quantityStep: number;
  errors?: VersionsControlErrors;
  disabled?: boolean;
  /** Distinguishes two controls on one screen. */
  idPrefix?: string;
  /** The count was typed: the new text and what it means (1 to 26). */
  onCountChange: (raw: string, next: VersionsValue) => void;
  onValueChange: (next: VersionsValue) => void;
}) {
  const countError = countRawError(label, countRaw);
  const shownCountError = errors.count ?? countError;
  const countErrorId = `${idPrefix}-count-error`;

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-3">
        <input
          type="number"
          step={1}
          min={1}
          max={MAX_VERSIONS}
          value={countRaw}
          aria-label={label}
          aria-invalid={shownCountError ? true : undefined}
          aria-describedby={shownCountError ? countErrorId : undefined}
          disabled={disabled}
          onChange={(e) => {
            const raw = e.target.value;
            onCountChange(
              raw,
              withVersionCount(
                value,
                countRawError(label, raw) ? 1 : versionCountOf(raw),
              ),
            );
          }}
          className="w-24 rounded-md border border-border bg-card px-3 py-1.5 text-sm tabular-nums"
        />
        {value.count < 2 && !shownCountError && (
          <span className="text-xs text-muted-foreground">
            One version. Add more for the same item in different artwork.
          </span>
        )}
      </div>
      {shownCountError && (
        <p id={countErrorId} className="text-sm text-red-600">
          {shownCountError}
        </p>
      )}
      {value.count >= 2 && !countError && (
        <VersionsSplit
          value={value}
          quantity={quantity}
          quantityStep={quantityStep}
          errors={errors}
          disabled={disabled}
          idPrefix={idPrefix}
          onValueChange={onValueChange}
        />
      )}
    </div>
  );
}

/**
 * How a line of two or more versions shares its quantity: the split choice, the even share or the boxes to enter, and
 * what is wrong with them. Alone in *Change quantities…*, where the count is fixed (the count is the spec).
 */
export function VersionsSplit({
  value,
  quantity,
  quantityStep,
  errors = {},
  disabled = false,
  idPrefix = "versions",
  labels,
  onValueChange,
}: {
  value: VersionsValue;
  quantity: number;
  quantityStep: number;
  errors?: VersionsControlErrors;
  disabled?: boolean;
  idPrefix?: string;
  /** The versions' labels when they have been renamed; A, B, C… by default. */
  labels?: readonly string[];
  onValueChange: (next: VersionsValue) => void;
}) {
  const preview = versionsProblem(value, quantity, quantityStep);
  const quantitiesError = errors.quantities ?? preview?.message;
  const quantitiesErrorId = `${idPrefix}-quantities-error`;
  const splitErrorId = `${idPrefix}-split-error`;
  const shares = versionQuantities(value, quantity, quantityStep);
  const labelOf = (position: number) =>
    labels?.[position] ?? versionLabel(position + 1);
  const sum = value.entered
    .slice(0, value.count)
    .reduce<number>((total, box) => total + (box ?? 0), 0);

  const setSplit = (split: VersionSplitMode) =>
    onValueChange(withVersionSplit(value, split, quantity, quantityStep));

  return (
    <div className="space-y-2">
      <RadioGroup
        aria-label="Split between versions"
        aria-invalid={errors.split ? true : undefined}
        aria-describedby={errors.split ? splitErrorId : undefined}
        value={value.split}
        onValueChange={(next) => setSplit(next as VersionSplitMode)}
        disabled={disabled}
        className="flex flex-wrap gap-x-5 gap-y-1"
      >
        {(
          [
            ["even", "Split evenly"],
            ["entered", "Enter quantities"],
          ] as const
        ).map(([mode, text]) => (
          <label
            key={mode}
            className="flex items-center gap-2 text-sm"
            htmlFor={`${idPrefix}-split-${mode}`}
          >
            <RadioGroupItem value={mode} id={`${idPrefix}-split-${mode}`} />
            {text}
          </label>
        ))}
      </RadioGroup>
      {errors.split && (
        <p id={splitErrorId} className="text-sm text-red-600">
          {errors.split}
        </p>
      )}

      {value.split === "even" && shares !== null && (
        <p className="text-sm tabular-nums" data-testid="versions-summary">
          {describeVersions(shares, "even")}
        </p>
      )}

      {value.split === "entered" && (
        <div
          role="group"
          aria-label="Quantity of each version"
          className="grid grid-cols-2 gap-x-4 gap-y-1.5 sm:grid-cols-3"
        >
          {Array.from({ length: value.count }, (_, position) => {
            const box = value.entered[position] ?? null;
            return (
              <label key={position} className="flex items-center gap-2 text-sm">
                <span className="w-5 shrink-0 font-medium text-muted-foreground">
                  {labelOf(position)}
                </span>
                <input
                  type="number"
                  min={1}
                  step={1}
                  inputMode="numeric"
                  value={box ?? ""}
                  aria-label={`Version ${labelOf(position)} quantity`}
                  aria-invalid={
                    quantitiesError ||
                    preview?.field === `version_quantities.${position}`
                      ? true
                      : undefined
                  }
                  aria-describedby={
                    quantitiesError ? quantitiesErrorId : undefined
                  }
                  disabled={disabled}
                  onChange={(e) => {
                    const text = e.target.value.trim();
                    const next = Array.from(
                      { length: value.count },
                      (_, i) => value.entered[i] ?? null,
                    );
                    next[position] = text === "" ? null : Number(text);
                    onValueChange({ ...value, entered: next });
                  }}
                  className="w-24 rounded-md border border-border bg-card px-2 py-1 text-sm tabular-nums"
                />
              </label>
            );
          })}
        </div>
      )}

      {value.split === "entered" && !quantitiesError && (
        <p
          className="text-xs text-muted-foreground tabular-nums"
          data-testid="versions-sum"
        >
          {sum.toLocaleString("en-GB")} of {quantity.toLocaleString("en-GB")}
        </p>
      )}

      {quantitiesError && (
        <p id={quantitiesErrorId} role="alert" className="text-sm text-red-600">
          {quantitiesError}
          {value.split === "even" && (
            <>
              {" "}
              <button
                type="button"
                className="underline"
                onClick={() => setSplit("entered")}
              >
                Enter quantities
              </button>
            </>
          )}
        </p>
      )}
    </div>
  );
}

/** The count's own rule, said as the item field says it; empty is one version, not wrong. */
function countRawError(label: string, raw: string): string | undefined {
  const text = raw.trim();
  if (text === "") return undefined;
  if (!/^-?\d+$/.test(text)) return `${label} must be a whole number`;
  const count = Number(text);
  if (count < 1) return `${label} ${count} is below the minimum of 1`;
  if (count > MAX_VERSIONS)
    return `${label} ${count} exceeds the maximum of ${MAX_VERSIONS}`;
  return undefined;
}
